/**
 * The rules, on demand.
 *
 * DESIGN.md's brief is "less explanation, more room for the room", and that
 * still holds for the *flow*: no screen narrates itself, and nothing here
 * appears unless someone asks for it. But the game is unusual enough that the
 * person handed a phone halfway through a party has no way to find out what the
 * countdown is for or why they are being asked to guess. One `i` in the corner
 * of the two screens people sit on before the game starts — the front door and
 * the lobby — answers that without putting a word of onboarding in the flow.
 *
 * It is a sheet rather than a route because the lobby is inside `/r/:roomCode`,
 * where `PhaseSync` owns the URL: navigating anywhere else would be undone on
 * the next render, and unmounting the room route would drop the socket. The
 * sheet leaves the room mounted and the socket connected.
 *
 * Every number in it is read from `@idgame/protocol`, so the rules on screen
 * cannot drift from the rules the server enforces.
 *
 * The *sentences* can, and did. This sheet was written one commit before
 * prediction was redesigned, and arrived describing picks you could "change as
 * often as you like" and a countdown that always ran — by which time Submit was
 * a one-way lock and the countdown had become an exception. The numbers were
 * never the risk. So the rules most likely to go stale are asserted in
 * `client/test/howToPlay.test.tsx` against the prose, not just the constants.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LOCK_COUNTDOWN_MS,
  MAX_PLAYERS,
  MIN_PLAYERS,
  PERFECT_READ_MULTIPLIER,
  POINTS_BASE,
  POINTS_ROUND_STEP,
  QUESTION_NUMBER_MAX,
} from '@idgame/protocol';
import { Button, Eyebrow } from './ui.js';

const LOCK_SECONDS = Math.round(LOCK_COUNTDOWN_MS / 1000);

const STEPS: { title: string; body: string }[] = [
  {
    title: 'Everyone gets a card',
    body: 'Scan the code, take a selfie, add a first name. Your face becomes your license card — that is the thing the room ranks.',
  },
  {
    title: 'One player picks a number',
    body: `Numbers run 1–${QUESTION_NUMBER_MAX} and each one hides a question about the group. The question opens on every phone at once, and that number is spent for the rest of the game.`,
  },
  {
    title: 'They rank the whole room',
    body: 'The turn player drags every player — themselves included — into order, first to last. Nobody else sees it while it is being built.',
  },
  {
    title: 'Everyone else calls the ends',
    body: 'While they rank, you privately pick who they will put first and who they will put last, on one screen, in either order. Change your mind as often as you like — Submit is what makes it final, and it tells the room to stop waiting for you.',
  },
  {
    title: `A ${LOCK_SECONDS}-second clock, if it is needed`,
    body: `If everyone has submitted by the time the ranking lands, the turn goes straight to the reveal and there is no countdown at all. Otherwise whoever is left gets ${LOCK_SECONDS} seconds. Both ends filled scores whether or not you pressed Submit; an end left empty scores nothing.`,
  },
  {
    title: 'Reveal, then explain out loud',
    body: 'The ranking lands top and bottom first, and every prediction is shown by name. Then the turn player justifies the whole thing to the room — that argument is the actual game.',
  },
];

/* -------------------------------------------------------------------------- */
/* The button                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The `i` itself. Drop it in a `Screen`'s `meta` slot — the status row's right
 * corner — on any screen that is not mid-turn.
 *
 * The sheet is portalled to `document.body` for two reasons: the `meta` slot is
 * a paragraph, and a full-height panel is not phrasing content; and the phone
 * column clips its overflow, which a sheet living inside it would inherit.
 */
export function HowToPlayButton() {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);

  // Focus goes back where it came from, or the next tab lands at the top of the
  // document instead of on the corner the reader was already in.
  const close = useCallback(() => {
    setOpen(false);
    trigger.current?.focus();
  }, []);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="How to play"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="how-to-play-button"
        /* Square, not a disc: DESIGN.md §1 puts a 10px radius on buttons and
           999px on nothing. 44px is the floor for anything tappable. */
        className="rounded-btn flex h-11 w-11 items-center justify-center border border-line bg-cream text-base leading-none font-semibold text-ink normal-case"
      >
        <span aria-hidden="true">i</span>
      </button>
      {open ? <HowToPlaySheet onClose={close} /> : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* The sheet                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The panel on its own, so the gallery can shoot it without simulating a tap.
 * Ordinary use goes through `HowToPlayButton`.
 */
export function HowToPlaySheet({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButton.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-center bg-ink/45"
      // Tapping the ink outside the column closes it, the way tapping away from
      // anything else in this app does. The panel stops the click itself so a
      // tap on the copy is not a tap on the backdrop.
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="how-to-play"
        onClick={(event) => event.stopPropagation()}
        /* The safe-area insets are repeated here rather than inherited: the
           sheet is portalled to `body`, outside the `phone-column` that carries
           them, so without this the heading sits under the notch. */
        className="flex h-full w-full max-w-[var(--spacing-phone)] flex-col bg-sand pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 px-5 pt-4 pb-3">
          <div>
            <Eyebrow>The ID Game</Eyebrow>
            <h2
              id={titleId}
              className="mt-1 text-[1.75rem] leading-[1.15] font-semibold tracking-tight text-ink"
            >
              How to play.
            </h2>
            <p className="mt-1.5 text-[0.8125rem] leading-snug text-ink-soft">
              {MIN_PLAYERS}–{MAX_PLAYERS} players, one phone each, all in the same room.
            </p>
          </div>
          <button
            ref={closeButton}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-btn -mr-1 flex h-11 w-11 shrink-0 items-center justify-center border border-line bg-cream text-xl leading-none text-ink"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
          <ol className="flex flex-col gap-2">
            {STEPS.map((step, index) => (
              <li
                key={step.title}
                className="rounded-card grid grid-cols-[1.75rem_1fr] gap-x-3 border border-line bg-cream p-4"
              >
                <span className="text-[0.8125rem] leading-tight font-semibold text-green tabular-nums">
                  {index + 1}
                </span>
                <div>
                  <p className="text-[0.9375rem] leading-tight font-semibold text-ink">
                    {step.title}
                  </p>
                  <p className="mt-1.5 text-[0.8125rem] leading-relaxed text-ink-soft">
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <section className="rounded-card mt-4 border border-line bg-cream p-4">
            <Eyebrow as="h3">Scoring</Eyebrow>
            <p className="mt-2 text-[0.8125rem] leading-relaxed text-ink-soft">
              {POINTS_BASE} points for a correct call, and {POINTS_ROUND_STEP} more every round
              after the first. Get both ends right and the whole turn is multiplied by{' '}
              {PERFECT_READ_MULTIPLIER}. The turn player scores nothing on their own turn — they
              wrote the answer — and the leaderboard only appears at the end of a round.
            </p>
          </section>

          <p className="mt-4 text-[0.8125rem] leading-relaxed text-ink-soft">
            A round is one full pass: everybody takes exactly one turn. The host picks how many
            rounds to play and can end the game early.
          </p>
        </div>

        <footer className="shrink-0 px-5 pt-2 pb-4">
          <Button variant="secondary" onClick={onClose}>
            Got it
          </Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
